import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  Clock,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Banknote,
} from "lucide-react";

const keyPolicies = [
  {
    icon: Shield,
    title: "Escrow Protection",
    description:
      "All payments are held in escrow until milestone completion and client approval.",
  },
  {
    icon: Clock,
    title: "Milestone-Based",
    description:
      "Projects are divided into clear milestones with defined deliverables and acceptance criteria.",
  },
  {
    icon: CheckCircle2,
    title: "Satisfaction Guarantee",
    description:
      "60-day post-delivery support for any bugs or issues directly caused by our work.",
  },
  {
    icon: Banknote,
    title: "Fair Billing",
    description:
      "You only pay for completed work. Unused escrow funds are returned on withdrawal.",
  },
];

export default function PaymentPolicyPage() {
  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <Badge variant="info" className="mb-4">
              Payments & Protection
            </Badge>
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
              Payment Policy
            </h1>
            <p className="text-muted-foreground mb-8">
              Last updated: December 30, 2024
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-12">
              {keyPolicies.map((policy) => (
                <Card key={policy.title} className="glass-card border-gradient">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <policy.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{policy.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {policy.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="prose prose-invert prose-lg max-w-none">
              <h2 className="font-display text-2xl font-bold mt-8 mb-4">
                1. Escrow-Based Payment System
              </h2>
              <p className="text-muted-foreground mb-4">
                To protect both parties, Echopeak Labs uses an escrow-based
                payment system for all project engagements:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Upfront Escrow Deposit:</strong> Before work begins,
                  clients deposit the agreed project fee (or milestone fee) into
                  a secure escrow account.
                </li>
                <li>
                  <strong>Funds Held Securely:</strong> Escrow funds are held by
                  a third-party payment processor and are not released to
                  Echopeak Labs until milestone completion and client approval.
                </li>
                <li>
                  <strong>Release Upon Approval:</strong> Once a milestone is
                  delivered and approved by the client, the corresponding escrow
                  funds are released to Echopeak Labs.
                </li>
                <li>
                  <strong>Dispute Resolution:</strong> In case of disputes, the
                  escrow provider acts as a neutral party to mediate and ensure
                  fair resolution.
                </li>
              </ul>

              <h2 className="font-display text-2xl font-bold mt-8 mb-4">
                2. Milestone Structure & Deliverables
              </h2>
              <p className="text-muted-foreground mb-4">
                All projects are structured around clearly defined milestones:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Scope Definition:</strong> Each milestone has a
                  written scope of work, acceptance criteria, and timeline
                  agreed upon before work begins.
                </li>
                <li>
                  <strong>Deliverable Review:</strong> Clients have 5 business
                  days to review milestone deliverables and provide feedback.
                </li>
                <li>
                  <strong>Approval or Revision:</strong> Clients may approve the
                  milestone (releasing escrow) or request revisions within the
                  agreed scope.
                </li>
              </ul>

              <Card className="glass-card border-gradient my-8">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-6 w-6 text-amber-400 shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-2">
                        Revision Policy: Preventing Infinite Changes
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        To ensure project momentum and fair compensation:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                        <li>
                          <strong>Two Revision Rounds Included:</strong> Each
                          milestone includes up to two (2) rounds of revisions
                          within the original scope at no additional cost.
                        </li>
                        <li>
                          <strong>Scope Creep Protection:</strong> Requests that
                          fall outside the agreed milestone scope are treated as
                          new work and quoted separately.
                        </li>
                        <li>
                          <strong>Revision Timeout:</strong> If no feedback is
                          received within 10 business days of delivery, the
                          milestone is considered approved and escrow funds are
                          released.
                        </li>
                        <li>
                          <strong>Additional Revisions:</strong> Further
                          revisions beyond the included rounds are billed at our
                          standard hourly rate.
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <h2 className="font-display text-2xl font-bold mt-8 mb-4">
                3. Package Request Withdrawal Policy
              </h2>
              <p className="text-muted-foreground mb-4">
                Clients may withdraw a package request at any time, subject to
                the following terms:
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Before Work Begins
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Full Refund:</strong> If withdrawal occurs before any
                  work has commenced (within 24 hours of escrow deposit and
                  before project kickoff), 100% of escrow funds are returned.
                </li>
                <li>
                  <strong>Planning Fee:</strong> If withdrawal occurs after
                  project kickoff but before development begins, a planning fee
                  of 10% (minimum $500) is retained to cover discovery, scoping,
                  and resource allocation.
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Mid-Milestone Withdrawal
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Work Compensation:</strong> Echopeak Labs is entitled
                  to compensation for all work completed up to the withdrawal
                  date, calculated on a pro-rata basis of the milestone fee.
                </li>
                <li>
                  <strong>Partial Deliverables:</strong> Any work-in-progress
                  will be delivered to the client in its current state (code,
                  designs, documentation).
                </li>
                <li>
                  <strong>Minimum Charge:</strong> A minimum charge of 25% of
                  the current milestone fee applies to cover setup,
                  coordination, and knowledge transfer costs.
                </li>
                <li>
                  <strong>Remaining Funds:</strong> Unused escrow funds (after
                  deducting completed work) are returned within 5-7 business
                  days.
                </li>
              </ul>

              <Card className="glass-card border-gradient my-8">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <RefreshCw className="h-6 w-6 text-blue-400 shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-2">
                        Rapid Withdrawal Prevention
                      </h3>
                      <p className="text-muted-foreground">
                        To prevent abuse of the escrow system (e.g., immediate
                        withdrawal after payment to access planning documents or
                        project setup), a <strong>24-hour cooling period</strong>{" "}
                        applies after escrow deposit before withdrawal is
                        permitted. During this period, the project kickoff call
                        is scheduled but no billable work begins.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <h2 className="font-display text-2xl font-bold mt-8 mb-4">
                4. Post-Delivery Satisfaction Guarantee
              </h2>
              <p className="text-muted-foreground mb-4">
                Echopeak Labs stands behind the quality of our work. Our
                satisfaction guarantee ensures you receive software that works
                as intended:
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                60-Day Support Period
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Bug Fixes Included:</strong> For 60 days following
                  final milestone approval, we will fix any bugs, defects, or
                  malfunctions that are directly caused by our code—at no
                  additional charge.
                </li>
                <li>
                  <strong>Definition of Bug:</strong> A bug is defined as
                  functionality that does not work as specified in the approved
                  milestone scope or acceptance criteria.
                </li>
                <li>
                  <strong>Response Time:</strong> Critical bugs (system down,
                  data loss risk) are addressed within 24 hours. Non-critical
                  bugs are addressed within 5 business days.
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Complex Issue Handling
              </h3>
              <p className="text-muted-foreground mb-4">
                For issues requiring extended investigation or repair (estimated
                2+ weeks):
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Transparent Communication:</strong> We will provide a
                  detailed assessment of the issue, estimated timeline, and root
                  cause analysis within 48 hours of identification.
                </li>
                <li>
                  <strong>Free Remediation:</strong> If the issue is directly
                  caused by our work (confirmed bug, logic error, or
                  implementation mistake), remediation is provided at no cost
                  regardless of duration.
                </li>
                <li>
                  <strong>Interim Solutions:</strong> Where possible, we will
                  implement temporary workarounds to minimize business impact
                  while the permanent fix is developed.
                </li>
                <li>
                  <strong>Weekly Updates:</strong> For extended fixes, clients
                  receive weekly progress updates until resolution.
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                What Is Not Covered
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  Issues caused by client modifications to the delivered code
                </li>
                <li>
                  Problems arising from third-party services, APIs, or
                  infrastructure not under our control
                </li>
                <li>
                  Feature requests, enhancements, or changes to the original
                  scope
                </li>
                <li>
                  Issues caused by the client's hosting environment,
                  configuration, or deployment process (unless we managed
                  deployment)
                </li>
                <li>
                  Normal maintenance, updates, or security patches beyond the
                  support period
                </li>
              </ul>

              <h2 className="font-display text-2xl font-bold mt-8 mb-4">
                5. Payment Schedule & Methods
              </h2>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Accepted Methods:</strong> Credit/debit cards, bank
                  transfers, and major digital payment platforms.
                </li>
                <li>
                  <strong>Currency:</strong> All prices are quoted in USD unless
                  otherwise specified.
                </li>
                <li>
                  <strong>Invoice Timing:</strong> Invoices for approved
                  milestones are generated upon client approval and escrow
                  release.
                </li>
                <li>
                  <strong>Monthly Retainers:</strong> For retainer engagements,
                  payment is due at the beginning of each service month.
                </li>
              </ul>

              <h2 className="font-display text-2xl font-bold mt-8 mb-4">
                6. Dispute Resolution
              </h2>
              <p className="text-muted-foreground mb-4">
                We aim to resolve all disputes amicably and fairly:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Direct Communication:</strong> Both parties will first
                  attempt to resolve disputes through direct communication
                  within 10 business days.
                </li>
                <li>
                  <strong>Escrow Mediation:</strong> If unresolved, the escrow
                  provider may be engaged to mediate based on documented scope,
                  deliverables, and communications.
                </li>
                <li>
                  <strong>Good Faith:</strong> Both parties agree to act in good
                  faith and provide all relevant documentation to support their
                  position.
                </li>
              </ul>

              <h2 className="font-display text-2xl font-bold mt-8 mb-4">
                7. Contact
              </h2>
              <p className="text-muted-foreground">
                For questions about this Payment Policy or to discuss payment
                arrangements, please contact us at:
                <br />
                Email: billing@echopeaklabs.com
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

